import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCookie, faShieldAlt, faChartLine, faCog } from '@fortawesome/free-solid-svg-icons';

const CookiePolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const cookieTypes = [
    {
      id: 'tecnici',
      title: 'Cookie Tecnici',
      icon: faCog,
      color: 'from-blue-500 to-cyan-500',
      required: true,
      description: 'Necessari per il funzionamento del sito',
      cookies: [
        { name: 'session_id', purpose: 'Gestione della sessione utente', duration: 'Sessione' },
        { name: 'cookie_consent', purpose: 'Memorizzazione preferenze cookie', duration: '12 mesi' },
      ]
    },
    {
      id: 'analitici',
      title: 'Cookie Analitici',
      icon: faChartLine,
      color: 'from-purple-500 to-pink-500',
      required: false,
      description: 'Ci aiutano a capire come viene utilizzato il sito',
      cookies: [
        { name: '_ga', purpose: 'Google Analytics - Identificazione utenti', duration: '2 anni' },
        { name: '_gid', purpose: 'Google Analytics - Identificazione sessioni', duration: '24 ore' },
      ]
    },
    {
      id: 'marketing',
      title: 'Cookie di Marketing',
      icon: faCookie,
      color: 'from-orange-500 to-red-500',
      required: false,
      description: 'Utilizzati per mostrare annunci rilevanti',
      cookies: [
        { name: '_fbp', purpose: 'Facebook Pixel - Tracciamento conversioni', duration: '3 mesi' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="pt-28 md:pt-32 pb-12 bg-gradient-to-br from-primary to-secondary text-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl mb-6">
              <FontAwesomeIcon icon={faCookie} className="text-4xl" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Cookie Policy</h1>
            <p className="text-xl text-white/90">
              Informazioni sui cookie utilizzati e su come gestire le tue preferenze
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            {/* Intro */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-8">
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border-l-4 border-primary p-6 rounded-r-lg mb-8">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Ultimo aggiornamento:</strong> Dicembre 2024
                </p>
                <p className="text-sm text-gray-600">
                  Marchio di proprietà di <strong>Novastart Srl</strong> - P.IVA: 03373610785
                </p>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-6">Cosa sono i Cookie?</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                I cookie sono piccoli file di testo che i siti web visitati inviano al tuo dispositivo (computer, tablet, smartphone), 
                dove vengono memorizzati per essere ritrasmessi agli stessi siti alla successiva visita. I cookie sono utilizzati per 
                diverse finalità, hanno caratteristiche diverse e possono essere utilizzati sia dal titolare del sito che si sta 
                visitando, sia da terze parti.
              </p>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-100">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-3xl text-blue-600 mb-3" />
                  <h4 className="font-bold text-gray-800 mb-2">Sicuri</h4>
                  <p className="text-sm text-gray-600">Non possono contenere virus o malware</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-100">
                  <FontAwesomeIcon icon={faCog} className="text-3xl text-purple-600 mb-3" />
                  <h4 className="font-bold text-gray-800 mb-2">Personalizzabili</h4>
                  <p className="text-sm text-gray-600">Puoi gestire le tue preferenze</p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-xl border-2 border-orange-100">
                  <FontAwesomeIcon icon={faChartLine} className="text-3xl text-orange-600 mb-3" />
                  <h4 className="font-bold text-gray-800 mb-2">Utili</h4>
                  <p className="text-sm text-gray-600">Migliorano la tua esperienza</p>
                </div>
              </div>
            </div>

            {/* Cookie Types */}
            <div className="space-y-6 mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Tipologie di Cookie Utilizzati</h2>
              
              {cookieTypes.map((type, index) => (
                <motion.div
                  key={type.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(type.id)}
                    className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${type.color} text-white shadow-lg`}>
                        <FontAwesomeIcon icon={type.icon} className="text-xl" />
                      </div>
                      <div className="text-left">
                        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                          {type.title}
                          {type.required && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                              Necessari
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                    <FontAwesomeIcon 
                      icon={activeSection === type.id ? faChevronUp : faChevronDown} 
                      className="text-gray-400"
                    />
                  </button>

                  {activeSection === type.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-gray-100"
                    >
                      <div className="p-6 bg-gray-50">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 text-sm font-semibold text-gray-700">Nome</th>
                              <th className="text-left py-3 text-sm font-semibold text-gray-700">Finalità</th>
                              <th className="text-left py-3 text-sm font-semibold text-gray-700">Durata</th>
                            </tr>
                          </thead>
                          <tbody>
                            {type.cookies.map((cookie, idx) => (
                              <tr key={idx} className="border-b border-gray-100 last:border-0">
                                <td className="py-3 font-mono text-sm text-gray-800">{cookie.name}</td>
                                <td className="py-3 text-sm text-gray-600">{cookie.purpose}</td>
                                <td className="py-3 text-sm text-gray-600">{cookie.duration}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Cookie Management */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Come Gestire i Cookie</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Tramite il Browser</h3>
                  <p className="text-gray-600 mb-4">
                    Puoi gestire o eliminare i cookie in base alle tue preferenze. Ecco come fare sui browser più comuni:
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-primary transition-colors">
                      <h4 className="font-bold text-gray-800 mb-2">Google Chrome</h4>
                      <p className="text-sm text-gray-600">
                        Impostazioni → Privacy e sicurezza → Cookie e altri dati dei siti
                      </p>
                    </div>
                    <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-primary transition-colors">
                      <h4 className="font-bold text-gray-800 mb-2">Mozilla Firefox</h4>
                      <p className="text-sm text-gray-600">
                        Opzioni → Privacy e sicurezza → Cookie e dati dei siti web
                      </p>
                    </div>
                    <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-primary transition-colors">
                      <h4 className="font-bold text-gray-800 mb-2">Safari</h4>
                      <p className="text-sm text-gray-600">
                        Preferenze → Privacy → Gestisci dati siti web
                      </p>
                    </div>
                    <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-primary transition-colors">
                      <h4 className="font-bold text-gray-800 mb-2">Microsoft Edge</h4>
                      <p className="text-sm text-gray-600">
                        Impostazioni → Cookie e autorizzazioni sito → Gestisci ed elimina cookie
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                  <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Nota Importante
                  </h4>
                  <p className="text-sm text-amber-800">
                    Disabilitando i cookie tecnici, alcune funzionalità del sito potrebbero non funzionare correttamente. 
                    I cookie analitici e di marketing possono essere disabilitati senza compromettere la funzionalità del sito.
                  </p>
                </div>
              </div>
            </div>

            {/* Third Party Services */}
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Servizi di Terze Parti</h2>
              <p className="text-gray-600 mb-6">
                Utilizziamo i seguenti servizi di terze parti che potrebbero installare cookie sul tuo dispositivo:
              </p>

              <div className="space-y-4">
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-bold text-gray-800 mb-2">Google Analytics</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Servizio di analisi web fornito da Google Inc. per analizzare le statistiche di accesso e utilizzo del sito.
                  </p>
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:text-secondary underline"
                  >
                    Privacy Policy Google →
                  </a>
                </div>

                <div className="border-l-4 border-secondary pl-6">
                  <h4 className="font-bold text-gray-800 mb-2">Facebook Pixel</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Strumento di tracciamento fornito da Facebook per misurare l'efficacia delle campagne pubblicitarie.
                  </p>
                  <a 
                    href="https://www.facebook.com/privacy/explanation" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:text-secondary underline"
                  >
                    Privacy Policy Facebook →
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Hai Domande?</h2>
              <p className="text-gray-600 mb-6">
                Per qualsiasi domanda riguardo l'utilizzo dei cookie sul nostro sito, puoi contattarci:
              </p>
              <div className="space-y-2">
                <p className="text-gray-700">
                  <strong>Email:</strong> info@servizioclientienergia.com
                </p>
                <p className="text-gray-700">
                  <strong>Telefono:</strong> 06 60513535 - 06 92963501
                </p>
                <p className="text-gray-700">
                  <strong>Sede Legale:</strong> Via Privata Del Gonfalone 3, Milano (MI) 20123
                </p>
              </div>
            </div>

            {/* Last Update */}
            <div className="mt-8 text-center text-sm text-gray-500">
              <p>Questa Cookie Policy è stata aggiornata l'ultima volta a Dicembre 2024</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
